import React, { useMemo } from 'react'
import { Bell, Clock, ChevronDown, Bookmark, Check, UserRoundCog } from 'lucide-react'
import { Table } from '../../../components/ui/Table'
import { AgendaInteligenteController } from '../../../services/controllers/agendaInteligente'
import { useAgendaInteligente } from '../../../store/agendaInteligente/useAgendaInteligente'
import { TABLE_NAME } from '../constants'
import { ExpandedRow } from './ExpandedRow'
import { AppointmentActions } from './AppointmentActions'
import { AssinanteBadge, TutorPetCount, PackageBadge } from './styles'
import { ContactSelect } from './ContactSelect'
import { Tooltip } from '../../../components/ui/Tooltip'
import { formatName } from '../../../services/functions'
import { CustomerDetailsController } from '../../../services/controllers/customer'


export function AgendaInteligenteTable() {
  const CAgendaInteligente = AgendaInteligenteController()
  const CCustomerDetails = CustomerDetailsController()


  const filterQuery = useAgendaInteligente(st => st.data.filter.query)
  const isLoading = useAgendaInteligente(st => st.data.isLoading)
  const expandedId = useAgendaInteligente(st => st.data.expandedId)
  const contactDone = useAgendaInteligente(st => st.data.contactDone)

  const appointmentsList = CAgendaInteligente.getRows()


  const filtredList = appointmentsList.filter(appointment => {
    if (filterQuery) {
      const query = filterQuery.trim().toLowerCase()
      return (
        (appointment.cliente.nome || '').toLowerCase().includes(query) ||
        (appointment.pet.nome || '').toLowerCase().includes(query) ||
        (appointment.pet.raca || '').toLowerCase().includes(query) ||
        (appointment.status || '').toLowerCase().includes(query) ||
        (appointment.profissional || '').toLowerCase().includes(query) ||
        (appointment.servicos || []).some(s => s.toLowerCase().includes(query))
      )
    }
    return true
  })


  const rows = useMemo(() => {
    return filtredList.filter(appointment => {
      const filterStatus = useAgendaInteligente.getState().data.filter.status
      if (filterStatus !== "CANCELADO") return appointment.status !== "cancelado"
      return true
    })
  }, [filtredList])

  return (
    <Table.Root name={TABLE_NAME} customActions>
      <Table.Header columns={[
        'Hora:80px',
        'Tutor:minmax(100px, 1.4fr)',
        'Pet:minmax(80px, 1fr)',
        'Profissional:minmax(100px, 1.2fr)',
        'Contato:70px',
        'Status contato:188px',
        ':80px',
      ]} />
      <Table.Content isLoading={isLoading}>
        {!isLoading && rows.length === 0 && (
          <div style={{ padding: '40px', textAlign: 'center', fontSize: 14, color: '#9ca3af' }}>
            Nenhum agendamento encontrado.
          </div>
        )}

        {rows.map(appointment => {
          const open = expandedId === appointment.appointmentId
          const contatoFeito = !!contactDone[appointment.appointmentId]
          return (
            <React.Fragment key={appointment.appointmentId}>
              <Table.Line style={{ background: open ? '#fffbeb' : undefined }}>
                <Table.Item>
                  <button
                    onClick={() => CAgendaInteligente.toggleExpanded(appointment.appointmentId)}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}
                  >
                    <Clock size={12} style={{ color: '#9ca3af' }} />
                    {appointment.horario}
                    <ChevronDown size={12} style={{ color: '#9ca3af', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                  </button>
                </Table.Item>
                <Table.Item>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 13, fontWeight: 500, overflow: 'hidden', maxWidth: '100%' }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                      <button type="button" className="font-medium flex gap-1 items-center border-b border-transparent hover:border-current" onClick={() => CCustomerDetails.open(appointment.cliente.customerId)}>
                        <UserRoundCog aria-hidden="true" size={16} />
                        {formatName(appointment.cliente.nome)}
                      </button>
                    </span>
                    {appointment.scheduledPetsCount > 1 && (
                      <TutorPetCount>
                        {appointment.scheduledPetsCount} pets na lista
                      </TutorPetCount>
                    )}
                  </div>
                </Table.Item>
                <Table.Item>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden' }}>
                    <span style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {formatName(appointment.pet.nome)}
                    </span>
                    {appointment.assinante && (
                      <span className='flex justify-center items-center relative'>
                        <Tooltip.Root zIndex={16} name={`tooltip-subscriber-${appointment.appointmentId}`} side="top" sideOffset={12}>
                          <em className='px-2 py-1 not-italic'>Assinante</em>
                        </Tooltip.Root>
                        <Tooltip.Trigger tooltipName={`tooltip-subscriber-${appointment.appointmentId}`}>
                          <AssinanteBadge>
                            <Bell size={11} fill="currentColor" />
                          </AssinanteBadge>
                        </Tooltip.Trigger>
                      </span>
                    )}
                    {appointment.package && (
                      <span className='flex justify-center items-center relative'>
                        <Tooltip.Root zIndex={16} name={`tooltip-package-${appointment.appointmentId}`} side="top" sideOffset={12}>
                          <em className='px-2 py-1 not-italic'>Pacote</em>
                        </Tooltip.Root>
                        <Tooltip.Trigger tooltipName={`tooltip-package-${appointment.appointmentId}`}>
                          <PackageBadge>
                            <Bookmark size={11} fill="currentColor" />
                          </PackageBadge>
                        </Tooltip.Trigger>
                      </span>
                    )}
                  </div>
                </Table.Item>
                <Table.Item>
                  <span style={{ fontSize: 13, color: appointment.profissional ? 'inherit' : '#9ca3af' }}>
                    {appointment.profissional || '—'}
                  </span>
                </Table.Item>

                <Table.Item>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <div
                      role="checkbox"
                      aria-checked={contatoFeito}
                      tabIndex={0}
                      aria-label={contatoFeito ? 'Desmarcar contato' : 'Marcar contato'}
                      onClick={() => {
                        CAgendaInteligente.setContactDone(
                          appointment.appointmentId,
                          !contatoFeito
                        )
                      }}
                      onKeyDown={event => {
                        if (event.key !== 'Enter' && event.key !== ' ') return
                        event.preventDefault()
                        CAgendaInteligente.setContactDone(
                          appointment.appointmentId,
                          !contatoFeito
                        )
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <div style={{ width: 18, height: 18, borderRadius: 4, border: contatoFeito ? 'none' : '2px solid #ccc', background: contatoFeito ? '#10b981' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s' }}>
                        {contatoFeito && <Check size={11} color="white" strokeWidth={3} />}
                      </div>
                    </div>
                  </div>
                </Table.Item>
                <Table.Item>
                  <ContactSelect appointment={appointment} />
                </Table.Item>

                <Table.Item>
                  <AppointmentActions appointment={appointment} />
                </Table.Item>
              </Table.Line>

              {open && <ExpandedRow appointment={appointment} />}
            </React.Fragment>
          )
        })}
      </Table.Content>
    </Table.Root >
  )
}
